import React, { useCallback, useState } from "react";
import { Group } from "@visx/group";
import { Bar } from "@visx/shape";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Text } from "@visx/text";
import { Tooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { localPoint } from "@visx/event";
interface FinancialReportProps {
  expenses: number;
  income: number;
}
export const FinancialReport: React.FC<FinancialReportProps> = ({
  expenses,
  income
}) => {
  const width = 300;
  const height = 300;
  const margin = {
    top: 20,
    bottom: 50,
    left: 20,
    right: 20
  };
  const [tooltipData, setTooltipData] = useState<null | {
    name: string;
    value: number;
  }>(null);
  const [tooltipLeft, setTooltipLeft] = useState<number | null>(null);
  const [tooltipTop, setTooltipTop] = useState<number | null>(null);
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };
  const handleTooltip = useCallback((event: React.MouseEvent<SVGRectElement, MouseEvent>, data: {
    name: string;
    value: number;
  }) => {
    setTooltipData(data);
    setTooltipLeft(event.pageX - 8);
    setTooltipTop(event.pageY - 8);
  }, []);
  const data = [{
    name: "Cheltuieli",
    value: expenses
  }, {
    name: "Venituri",
    value: income
  }];
  const xScale = scaleBand<string>({
    range: [0, width - margin.left - margin.right],
    round: true,
    domain: data.map(d => d.name),
    padding: 0.4
  });
  const yScale = scaleLinear<number>({
    range: [height - margin.top - margin.bottom, 0],
    round: true,
    domain: [0, Math.max(expenses, income)]
  });
  return <div style={{
    fontFamily: "Arial, sans-serif",
    lineHeight: "1.5"
  }} className="drop-shadow-lg" data-sentry-component="FinancialReport" data-sentry-source-file="FinancialReport.tsx">
      <svg width={width} height={height} data-sentry-element="svg" data-sentry-source-file="FinancialReport.tsx">
        <defs data-sentry-element="defs" data-sentry-source-file="FinancialReport.tsx">
          <linearGradient id="gradient" x1="0%" y1="100%" x2="0%" y2="0%" data-sentry-element="linearGradient" data-sentry-source-file="FinancialReport.tsx">
            <stop offset="0%" stopColor="#6ee7b7e0" data-sentry-element="stop" data-sentry-source-file="FinancialReport.tsx" />
            <stop offset="100%" stopColor="#0c8e69df" data-sentry-element="stop" data-sentry-source-file="FinancialReport.tsx" />
          </linearGradient>
          <linearGradient id="gradientExpenses" x1="0%" y1="100%" x2="0%" y2="0%" data-sentry-element="linearGradient" data-sentry-source-file="FinancialReport.tsx">
            <stop offset="0%" stopColor="#ff0000e0" data-sentry-element="stop" data-sentry-source-file="FinancialReport.tsx" />
            <stop offset="100%" stopColor="#c03434df" data-sentry-element="stop" data-sentry-source-file="FinancialReport.tsx" />
          </linearGradient>
          <linearGradient id="gradientHover" x1="0%" y1="100%" x2="0%" y2="0%" data-sentry-element="linearGradient" data-sentry-source-file="FinancialReport.tsx">
            <stop offset="0%" stopColor="#6ee7b7" data-sentry-element="stop" data-sentry-source-file="FinancialReport.tsx" />
            <stop offset="100%" stopColor="#42dbaf" data-sentry-element="stop" data-sentry-source-file="FinancialReport.tsx" />
          </linearGradient>
          <filter id="glow" data-sentry-element="filter" data-sentry-source-file="FinancialReport.tsx">
            <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#2dd4bfe0" data-sentry-element="feDropShadow" data-sentry-source-file="FinancialReport.tsx" />
          </filter>
          <filter id="glowExpenses" data-sentry-element="filter" data-sentry-source-file="FinancialReport.tsx">
            <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#ff0000e0" data-sentry-element="feDropShadow" data-sentry-source-file="FinancialReport.tsx" />
          </filter>
        </defs>
        <Group top={margin.top} left={margin.left} data-sentry-element="Group" data-sentry-source-file="FinancialReport.tsx">
          {data.map((d, i) => <React.Fragment key={i}>
              <Bar x={xScale(d.name)} y={yScale(d.value)} height={yScale(0) - yScale(d.value)} width={xScale.bandwidth()} fill={d.name === "Cheltuieli" ? "url(#gradientExpenses)" : "url(#gradient)"} style={{
            transition: "opacity 0.3s",
            opacity: d.name === hoveredBar ? 1 : 0.8
          }} rx={8} ry={8} onMouseMove={event => handleTooltip(event, d)} onMouseEnter={() => setHoveredBar(d.name)} onMouseLeave={() => {
            setHoveredBar(null);
            setTooltipData(null);
          }} />
              <Text x={xScale(d.name)! + xScale.bandwidth() / 2} y={yScale(d.value) + 24} fill="#fff" textAnchor="middle" verticalAnchor="end" style={{
            pointerEvents: "none",
            userSelect: "none"
          }}>
                {formatNumber(d.value)}
              </Text>
              <Text x={xScale(d.name)! + xScale.bandwidth() / 2} y={height - margin.bottom} fill="#fff" textAnchor="middle" verticalAnchor="start">
                {d.name}
              </Text>
            </React.Fragment>)}
        </Group>
      </svg>
      {tooltipData &&
    // eslint-disable-next-line @shopify/jsx-no-hardcoded-content
    <TooltipWithBounds top={tooltipTop!} left={tooltipLeft!} style={defaultStyles}>
          <b>{tooltipData.name}</b>: {`${formatNumber(tooltipData.value)}`}
        </TooltipWithBounds>}
    </div>;
};